/**
 * Eff_status pill badge per F-FE-CORPUS-BROWSER §3.1 + §3.4.
 *
 * MD-31 Phase 1 C7 deuteranopia mitigation (2026-05-27):
 *   HHL variant gets MULTI-CUE differentiator (color + border + icon)
 *   so users with red-green color vision deficiency can distinguish HHL
 *   from CHL/Tango orange accent CTA even when amber hue washes out.
 *
 *   - border: 2px solid var(--asklaw-warning)
 *   - inline AlertTriangle icon 14px before label text
 *   - aria-label expand: "Hết hiệu lực - cảnh báo" (KHONG chỉ "HHL")
 *
 * Colors locked:
 *   CHL    bg-green-100  text-green-800  "Còn hiệu lực"
 *   HHL    bg-amber-100  text-amber-800  "Hết hiệu lực"     (multi-cue)
 *   HHL1P  bg-amber-100  text-amber-800  "Hết hiệu lực một phần" (multi-cue)
 *   TNHL   bg-blue-100   text-blue-800   "Tạm ngưng hiệu lực"
 *   CHHL   bg-slate-100  text-slate-800  "Chưa có hiệu lực"
 *
 * VN copy uses full diacritics. Dark mode uses *-900/-100 swap via inline CSS.
 */

import type { EffStatusCode } from '../../lib/corpus-api';
import { EFF_STATUS_LABEL_VI } from '../../lib/corpus-api';

const PALETTE: Record<string, { bg: string; fg: string; bgDark: string; fgDark: string }> = {
  CHL: { bg: '#dcfce7', fg: '#166534', bgDark: '#14532d', fgDark: '#bbf7d0' },
  HHL: { bg: '#fef3c7', fg: '#78350f', bgDark: '#78350f', fgDark: '#fde68a' },
  HHL1P: { bg: '#fef3c7', fg: '#92400e', bgDark: '#78350f', fgDark: '#fde68a' },
  TNHL: { bg: '#dbeafe', fg: '#1e40af', bgDark: '#1e3a8a', fgDark: '#bfdbfe' },
  CHHL: { bg: '#f1f5f9', fg: '#334155', bgDark: '#1e293b', fgDark: '#cbd5e1' },
  CCHL: { bg: '#f1f5f9', fg: '#334155', bgDark: '#1e293b', fgDark: '#cbd5e1' },   // same as CHHL (not-yet-in-force)
  TNHL1P: { bg: '#dbeafe', fg: '#1e40af', bgDark: '#1e3a8a', fgDark: '#bfdbfe' }, // same as TNHL
  KCPH: { bg: '#f1f5f9', fg: '#475569', bgDark: '#1e293b', fgDark: '#cbd5e1' },   // neutral (not published)
  KXĐ: { bg: '#f1f5f9', fg: '#475569', bgDark: '#1e293b', fgDark: '#cbd5e1' },    // neutral (undetermined)
  KXD: { bg: '#f1f5f9', fg: '#475569', bgDark: '#1e293b', fgDark: '#cbd5e1' },    // KXD spelling variant
  UNKNOWN: { bg: '#f1f5f9', fg: '#475569', bgDark: '#1e293b', fgDark: '#cbd5e1' },
};

// Codes triggering multi-cue (warning icon + bold border)
const WARN_CODES = new Set(['HHL', 'HHL1P']);

export function EffStatusBadge({
  status,
  size = 'sm',
}: {
  status: EffStatusCode | string | null | undefined;
  size?: 'sm' | 'md';
}) {
  const code = status || 'UNKNOWN';
  const pal = PALETTE[code] ?? PALETTE.UNKNOWN;
  const label = EFF_STATUS_LABEL_VI[code] ?? 'Chưa rõ';
  const isWarn = WARN_CODES.has(code);
  const isDark =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;

  // Multi-cue: HHL/HHL1P augment aria-label voi "cảnh báo" suffix
  const ariaLabel = isWarn
    ? `Tình trạng hiệu lực: ${label} - cảnh báo`
    : `Tình trạng hiệu lực: ${label}`;

  return (
    <span
      role="status"
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: size === 'md' ? '4px 12px' : '2px 10px',
        borderRadius: 9999,
        fontSize: size === 'md' ? 13 : 12,
        fontWeight: 600,
        background: isDark ? pal.bgDark : pal.bg,
        color: isDark ? pal.fgDark : pal.fg,
        // Multi-cue C7: HHL voi 2px border (CHL/CHHL/TNHL voi 0 border).
        border: isWarn
          ? `2px solid ${isDark ? pal.fgDark : pal.fg}`
          : '2px solid transparent',
        letterSpacing: '0.01em',
        whiteSpace: 'nowrap',
        fontFamily: 'var(--font-sans)',
      }}
    >
      {isWarn ? (
        <AlertTriangleIcon size={14} />
      ) : (
        <span
          aria-hidden
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'currentColor',
            display: 'inline-block',
            opacity: 0.65,
          }}
        />
      )}
      {label}
    </span>
  );
}

function AlertTriangleIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      style={{ flexShrink: 0 }}
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
